<template>
  <div class="popover-menu" ref="menuRef">
    <div class="popover-menu__trigger" ref="triggerRef" @click="toggle">
      <Icon :icon="triggerIcon" />
    </div>
    <Teleport to="body">
      <Transition name="popover">
        <div
          v-if="isOpen && itemsArray.length > 0"
          ref="dropdownRef"
          class="popover-menu__dropdown"
          :style="dropdownStyle"
        >
          <a
            v-for="(item, index) in itemsArray"
            :key="index"
            class="popover-menu__item"
            :class="{
              'popover-menu__item--disabled': item.disabled,
              'popover-menu__item--danger': item.danger,
            }"
            @click="handleItemClick(item, $event)"
          >
            <Icon v-if="item.icon" :icon="item.icon" class="popover-menu__item-icon" />
            <span>{{ item.label }}</span>
          </a>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue";

interface PopoverMenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface Props {
  items: PopoverMenuItem[];
  triggerIcon?: string;
  placement?:
    | "left"
    | "top"
    | "right"
    | "bottom"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  stayOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  triggerIcon: "mdi:dots-horizontal",
  placement: "bottom",
  stayOpen: false,
});

const itemsArray = computed(() => {
  return Array.isArray(props.items) ? props.items : [];
});

const isOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownStyle = ref<{
  position: "fixed";
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  zIndex: number;
}>({
  position: "fixed",
  zIndex: 10060,
});

const updateDropdownPosition = () => {
  if (!triggerRef.value || !dropdownRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 4;
  const dropdownHeight = dropdownRef.value.offsetHeight || 200;
  const dropdownWidth = dropdownRef.value.offsetWidth || 140;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  let top: string | undefined;
  let bottom: string | undefined;
  let left: string | undefined;
  let right: string | undefined;

  if (
    props.placement === "bottom" ||
    props.placement === "bottom-left" ||
    props.placement === "bottom-right"
  ) {
    const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;
    if (shouldShowAbove) {
      bottom = `${window.innerHeight - rect.top + spacing}px`;
    } else {
      top = `${rect.bottom + spacing}px`;
    }
  } else if (
    props.placement === "top" ||
    props.placement === "top-left" ||
    props.placement === "top-right"
  ) {
    const shouldShowBelow = spaceAbove < dropdownHeight + spacing && spaceBelow > spaceAbove;
    if (shouldShowBelow) {
      top = `${rect.bottom + spacing}px`;
    } else {
      bottom = `${window.innerHeight - rect.top + spacing}px`;
    }
  } else {
    top = `${rect.bottom + spacing}px`;
  }

  if (props.placement === "bottom-right" || props.placement === "top-right") {
    right = `${window.innerWidth - rect.right}px`;
  } else if (props.placement === "bottom-left" || props.placement === "top-left") {
    left = `${rect.left}px`;
  } else if (props.placement === "right") {
    left = `${rect.right + spacing}px`;
    top = `${rect.top}px`;
  } else if (props.placement === "left") {
    right = `${window.innerWidth - rect.left + spacing}px`;
    top = `${rect.top}px`;
  } else {
    right = `${window.innerWidth - rect.right}px`;
  }

  dropdownStyle.value = {
    position: "fixed",
    top,
    bottom,
    left,
    right,
    zIndex: 10060,
  };
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const handleItemClick = (item: PopoverMenuItem, event: MouseEvent) => {
  event.stopPropagation();
  if (!item.disabled) {
    item.onClick();
    if (!props.stayOpen) {
      isOpen.value = false;
    }
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  if (
    menuRef.value &&
    !menuRef.value.contains(target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(target)
  ) {
    isOpen.value = false;
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.popover-menu {
  position: relative;
  display: inline-block;
}

.popover-menu__trigger {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
}

.popover-menu__trigger:hover {
  background-color: #f3f4f6;
  color: #3e3efe;
}

.popover-menu__trigger .iconify {
  font-size: 20px;
  width: 20px;
  height: 20px;
}

.popover-menu__dropdown {
  position: fixed;
  z-index: 2000;
  min-width: 168px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 1px 2px rgba(16, 15, 73, 0.04),
    0 8px 24px -4px rgba(16, 15, 73, 0.1),
    0 20px 48px -12px rgba(16, 15, 73, 0.12);
  backdrop-filter: saturate(180%);
}

.popover-menu__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  margin: 0;
  font-family: "Instrument Sans", "Inter", sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.002em;
  color: #344054;
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease,
    transform 150ms ease;
}

.popover-menu__item:hover:not(.popover-menu__item--disabled),
.popover-menu__item:active:not(.popover-menu__item--disabled) {
  background-color: #f8f7ff;
  color: #3e3efe;
}

.popover-menu__item:active:not(.popover-menu__item--disabled) {
  transform: scale(0.98);
}

.popover-menu__item--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.popover-menu__item--danger {
  color: #dc2626;
}

.popover-menu__item--danger:hover:not(.popover-menu__item--disabled),
.popover-menu__item--danger:active:not(.popover-menu__item--disabled) {
  color: #b91c1c;
  background-color: #fef2f2;
}

.popover-menu__item-icon {
  width: 15px;
  height: 15px;
  font-size: 15px;
  flex-shrink: 0;
  color: #9ca3af;
  transition: color 150ms ease;
}

.popover-menu__item:hover:not(.popover-menu__item--disabled) .popover-menu__item-icon,
.popover-menu__item:active:not(.popover-menu__item--disabled) .popover-menu__item-icon {
  color: inherit;
}

.popover-menu__item--danger .popover-menu__item-icon {
  color: #f87171;
}

/* Entrance: subtle translate + fade, origin-aware */
.popover-enter-active,
.popover-leave-active {
  transition:
    opacity 180ms cubic-bezier(0.2, 0.7, 0.2, 1),
    transform 180ms cubic-bezier(0.2, 0.7, 0.2, 1);
  transform-origin: top right;
}

.popover-enter-from,
.popover-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
